/* eslint-disable react/no-unused-state */
// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';
import moment from 'moment';

import Select2, { DataSelect2 } from 'wdna-select2';
import ContainerModal from 'wdna-components/lib/modals/generics/ContainerModal';

import { GridApi, RowDataChangedEvent, GridReadyEvent, ICellRendererParams } from 'wdna-grid-table/lib/types';
import Table, { TableButtonsConfig } from 'wdna-components/lib/table/Table';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';
import { messageError, messageBoxWarning, messageBoxInfo, messageInfo, } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import { waste, UnloadDO } from '../../../requests';

interface Props {
  rowId?: number;
}

interface State {
  loading: boolean;
  update: string;
  showGrid: boolean;
  show: boolean;
  marked: number;

  verified: string;
  dateIni: string;
  dateEnd?: string;

  weight?: number;
  tare?: number;
  value?: any;

  data: UnloadDO[];
}

export default class WasteUnload extends React.Component<Props, State> {
  private pid: string;
  private buttons: TableButtonsConfig;
  private mounted: boolean;
  private gridApi: GridApi | undefined;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;

    this.buttons = {
      edit: true,
      del: true,
      toggleItem: true,
    };

    const dIni: Date = new Date();
    dIni.setDate(dIni.getDate() - 7);
    const dEnd: Date = new Date();

    this.state = {
      loading: false,
      update: window.getStr(),
      data: [],

      dateIni: moment(dIni).format('YYYY-MM-DD'),
      verified: 'all',

      marked: 0,
      showGrid: false,
      show: false,
    };
  }

  public componentDidMount = (): void => {
    this.mounted = true;
    this.setState({
      update: window.getStr(),
    });
  };

  public shouldComponentUpdate = (nextProps: Props, nextState: State): boolean => {
    return this.state.update !== nextState.update;
  }

  public componentWillUnmount = (): void => {
    this.mounted = false;
  };

  public load = (): void => {
    const { rowId } = this.props || {};

    const req: Record<string, any> = {};
    if (rowId !== undefined && !isNaN(rowId)) {
      req.containerId = rowId;
    }
    if (this.state.dateIni) {
      req.from = this.state.dateIni;
    }
    if (this.state.dateEnd) {
      req.to = this.state.dateEnd;
    }
    if (this.state.verified !== 'all') {
      req.verified = this.state.verified;
    }

    this.loadingStart();
    waste<UnloadDO[]>('unload_list', undefined, req)
      ?.then((response: UnloadDO[]) => {
        this.setState({
          update: window.getStr(),
          data: response,
        });
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      })
      .finally(() => {
        this.loadingFinish();
      });
  };

  public loadingStart = (): void => {
    this.setState({
      update: window.getStr(),
      loading: true,
    });
    window.store.dispatch(initProcess(this.pid));
  };

  public loadingFinish = (): void => {
    this.setState({
      update: window.getStr(),
      loading: false,
    });
    window.store.dispatch(finishProcess(this.pid));
  };

  public rowDataChanged = (_event: RowDataChangedEvent): void => {
    this.setState({
      update: window.getStr(),
      showGrid: true,
    });
  };

  public onEdit = (): void => {
    const { value, tare, weight } = this.state;
    const req: Record<string, any> = {};
    req.operation = 'set';

    if (weight !== null) {
      req.weight = weight;
    }
    if (tare !== null) {
      req.tare = tare;
    }

    messageBoxWarning('Verify', 'Do you want to verify this unload?', () => {
      this.loadingStart();
      waste('unload_verify', value.id, req)
        ?.then(() => {
          messageInfo('Success', 'Unload successfully verified', 5000);
          this.load();
        })
        .catch((error: any) => {
          messageError('Error', getErrorMessage(error));
        })
        .finally(() => {
          this.loadingFinish();
        });
    });
  };

  public onDelete = (value: UnloadDO): void => {
    messageBoxWarning('Verify', 'Do you want to unverify this unload?', () => {
      this.loadingStart();
      waste('unload_verify', String(value.id), { operation: 'del' })
        ?.then(() => {
          messageInfo('Success', 'Unload successfully unverified', 5000);
          this.load();
        })
        .catch((error: any) => {
          messageError('Error', getErrorMessage(error));
        })
        .finally(() => {
          this.loadingFinish();
        });
    });
  };

  public bulkVerify = (verify: boolean): void => {
    if (this.gridApi) {
      const selectedNodes: any[] = this.gridApi.getSelectedNodes();
      const promises: any[] = [];
      selectedNodes.forEach((row: any) => {
        if (!row.data || !row.data.id) return;
        if (verify) {
          promises.push(waste('unload_verify', row.data.id, { operation: 'set' }));
        } else {
          promises.push(waste('unload_verify', row.data.id, { operation: 'del' }));
        }
      });

      this.loadingStart();
      Promise.all(promises)
        .then(() => {
          messageInfo('Success', `Unloads successfully ${verify ? 'verified' : 'unverified'}`, 5000);
          this.load();
        })
        .catch((error: any) => {
          messageError('Error', getErrorMessage(error));
        })
        .finally(() => {
          this.loadingFinish();
        });
    }
  };

  public verifySelected = (): void => {
    messageBoxInfo('Activate', 'Are you sure you want to verify the selected unloads?', () => {
      this.bulkVerify(true);
    });
  };

  public unverifySelected = (): void => {
    messageBoxInfo('Deactivate', 'Are you sure you want to unverify the selected unloads?', () => {
      this.bulkVerify(false);
    });
  };

  public handleSelected = (): void => {
    let marked: number = 0;
    if (this.gridApi) {
      marked = this.gridApi.getSelectedNodes().length;
    }
    this.setState({
      update: window.getStr(),
      marked,
    });
  };

  public handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { value, name } = e.target;
    this.setState((prevState: State) => ({
      ...prevState,
      update: window.getStr(),
      [name]: value,
    }));
  };

  public customCellRender = (params: ICellRendererParams): React.ReactNode => {
    let { value } = params;
    const { colId } = params.colDef;

    if (colId === 'flags') {
      value = <div>{String(value).split('').map((p: any) => <i className="fa fa-circle" style={{ color: (p === '1' ? 'green' : 'red') }} />)}</div>;
    }

    return value;
  }

  public onGridReady = (event: GridReadyEvent): void => {
    // map the api
    this.gridApi = event.api;
  };

  public onClose = (): void => {
    this.setState({
      weight: undefined,
      tare: undefined
    }, () => {
      this.forceUpdate();
      this.setState({
        update: window.getStr(),
        show: false,
      });
    });
  };

  public onCustom = (value: UnloadDO): void => {
    this.setState({
      update: window.getStr(),
      show: true,
      value,
    });
  };

  public handleChange2 = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      this.setState((prevState: State) => ({
        ...prevState,
        [name]: checked,
      }));
    } else {
      this.setState((prevState: State) => ({
        ...prevState,
        [name]: value,
      }));
    }
  }

  public render(): JSX.Element {
    const { data, marked, showGrid, show, tare, weight } = this.state;

    const columnAlias: Record<string, string> = {
      id: 'ID',
      date: 'Date',
      weight: 'Weight',
      tare: 'Tare',
      company: 'Company',
      containerId: 'Container Id',
      containerCode: 'Code',
      containerName: 'Name',
      containerDescription: 'Description',
      containerType: 'Type',
      flags: 'Status',
      verified: 'Verified'
    };
    const columnStyles: Record<string, any> = {
      id: { textAlign: 'right' },
      date: { textAlign: 'center' },
      weight: { textAlign: 'right' },
      tare: { textAlign: 'right' },
      company: { textAlign: 'left' },
      containerId: { textAlign: 'right' },
      containerCode: { textAlign: 'right' },
      containerName: { textAlign: 'left' },
      containerDescription: { textAlign: 'left' },
      containerType: { textAlign: 'left' },
      flags: { textAlign: 'center' },
      verified: { textAlign: 'center' },
      actionButtons: { textAlign: 'center' },
    };

    const selectRowProps: Record<string, string> = {
      mode: 'checkbox',
    };
    const customColumnsDefs: Record<string, any> = {
      id: {
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        checkboxSelection: true,
        suppressSizeToFit: false,
        minWidth: 80,
        maxWidth: 80,
      },
    };

    return (
      <div id="content">
        <div className="row">
          <div className="col-xs-12 col-sm-6 col-md-4 col-lg-3">
            <label className="label">
              Verified
            </label><br />
            <Select2
              name="verified"
              data={[
                { id: 'all', text: 'ALL' },
                { id: 'true', text: 'VERIFIED' },
                { id: 'false', text: 'UNVERIFIED' },
              ]}
              value={this.state.verified}
              options={{
                placeholder: 'Select option',
              }}
              onChange={this.handleChange}
              style={{ width: '90%' }}
            />
          </div>
          <div className="col-xs-6 col-sm-6 col-md-6 col-lg-4">
            <div className="inline-controls">
              <i className="fa fa-calendar">
                <label className="label">
                  Date Ini
                </label>
              </i><br />
              <input
                type="date"
                name="dateIni"
                className="inputNew"
                value={this.state.dateIni}
                onChange={this.handleChange}
              />
            </div>
            <div className="inline-controls">
              <i className="fa fa-calendar">
                <label className="label">
                  Date End
                </label>
              </i><br />
              <input
                type="date"
                name="dateEnd"
                className="inputNew"
                value={this.state.dateEnd}
                onChange={this.handleChange}
              />
            </div>
            <div className="inline-controls">
              <button type="button" onClick={this.load} className="btn btn-default">
                GO
              </button>
            </div>
          </div>
          <div className="col-xs-6 col-sm-6 col-md-6 col-lg-4">
            <label className="label">
              {`Total Rows Selected: ${marked}`}
            </label>
            <br />
            <div className="inline-controls">
              <button type="button" className="btn btn-success" onClick={this.verifySelected}>
                <b>Verify selected</b>
              </button>
            </div>
            <div className="inline-controls">
              <button type="button" className="btn btn-danger" onClick={this.unverifySelected}>
                <b>Unverify selected</b>
              </button>
            </div>
          </div>
        </div>
        <div id="content" style={{ visibility: (showGrid ? 'visible' : 'hidden') }}>
          <Table
            data={data}
            alias={columnAlias}
            hiddenCols={[]}
            loading={this.state.loading}
            buttons={this.buttons}
            rowDataChanged={this.rowDataChanged}
            customCellStyles={columnStyles}
            customColumnsDefs={customColumnsDefs}
            customCellRender={this.customCellRender}
            onEdit={this.onCustom}
            onDelete={this.onDelete}
            // TODO: review
            // selectRow={selectRowProps}
            onGridReady={this.onGridReady}
            onSelectionChanged={this.handleSelected}
            height="calc(75vh - 6px)"
            selection="multiple"
            pagination={false}
            showRowNumber
          />
        </div>
        <ContainerModal
          fullScreenButton={false}
          show={show}
          title="Register Waste"
          onClose={this.onClose}
          modalClass="modal-small selector-modal"
          footerButtons={[
            {
              text: 'OK',
              className: 'btn btn-primary',
              onClick: this.onEdit,
            },
          ]}
        >
          <div className="row">
            <div className="col-md-3">
              <label className="label">
                <b>Weight:</b> &nbsp;
              </label>
              <input
                className="inputNew"
                type="number"
                value={weight}
                name="weight"
                onChange={this.handleChange2}
              />
            </div>
            <div className="col-md-3">
              <label className="label">
                <b>Tare:</b> &nbsp;
              </label>
              <input
                className="inputNew"
                type="number"
                value={tare}
                name="tare"
                onChange={this.handleChange2}
              />
            </div>
          </div>
        </ContainerModal>
      </div>
    );
  }
}
